/**
 * Settings gear → Update: force the browser onto the newest deploy.
 *
 * Same contract as admin: probe SW update → bust HTTP shell caches →
 * clear caches/unregister → cache-bust hard navigate.
 * Mid-payment: blocked (no silent reload).
 */

import { bustCustomerHttpShellCaches } from './bustCustomerHttpShellCaches.js';
import { clearCustomerPwaClientState } from './clearCustomerPwaClientState.js';
import { isPaymentPending } from './paymentPendingGate.js';

export const CUSTOMER_PWA_RECOVERY_QUERY = '_pwa';

export type ForceCustomerAppUpdateOutcome =
  | 'started'
  | 'blocked_payment'
  | 'offline';

export function buildCustomerPwaCacheBustUrl(nowMs: number = Date.now()): string {
  const url = new URL(window.location.href);
  url.searchParams.set(CUSTOMER_PWA_RECOVERY_QUERY, String(nowMs));
  return url.toString();
}

/** Strip recovery query param after successful boot — no reload loop. */
export function stripCustomerPwaRecoveryQueryFromUrl(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  const url = new URL(window.location.href);
  if (!url.searchParams.has(CUSTOMER_PWA_RECOVERY_QUERY)) {
    return false;
  }
  url.searchParams.delete(CUSTOMER_PWA_RECOVERY_QUERY);
  const nextSearch = url.searchParams.toString();
  const nextUrl =
    url.pathname + (nextSearch.length > 0 ? `?${nextSearch}` : '') + url.hash;
  window.history.replaceState(null, '', nextUrl);
  return true;
}

async function probeServiceWorkerUpdate(): Promise<void> {
  if (!('serviceWorker' in navigator) || navigator.serviceWorker == null) {
    return;
  }
  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (registration === undefined) {
      return;
    }
    await registration.update();
    if (registration.waiting !== null) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  } catch {
    // Hard clear below still recovers stuck shells.
  }
}

export interface ForceCustomerAppUpdateDeps {
  readonly clearClientState: () => Promise<void>;
  readonly replace: (url: string) => void;
  readonly nowMs?: number;
  readonly bustHttpShellCaches?: () => Promise<void>;
}

export async function runForceCustomerAppUpdate(
  deps: ForceCustomerAppUpdateDeps,
): Promise<void> {
  await probeServiceWorkerUpdate();
  // Default bust includes `location.pathname` (deep document) when available.
  await (deps.bustHttpShellCaches ?? bustCustomerHttpShellCaches)();
  await deps.clearClientState();
  deps.replace(buildCustomerPwaCacheBustUrl(deps.nowMs ?? Date.now()));
}

/**
 * User-initiated update from CustomerSettingsSheet.
 */
export async function forceCustomerAppUpdateFromSettings(): Promise<ForceCustomerAppUpdateOutcome> {
  if (isPaymentPending()) {
    return 'blocked_payment';
  }
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    return 'offline';
  }

  await runForceCustomerAppUpdate({
    clearClientState: async () => {
      await clearCustomerPwaClientState();
    },
    replace: (url) => {
      window.location.replace(url);
    },
  });
  return 'started';
}
